import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthService } from 'app/auth/services/AuthService';
import { Telemetry } from 'app/services/telemetry/Telemetry';

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closing' | 'closed' | 'error';

const AUTH_CLOSE_CODES = [1008, 4001, 4401];
const DEFAULT_MAX_RECONNECT = 10;

type WsConnectionOptions = {
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  extraHeaders?: Record<string, string>;
  buildUrl?: (baseUrl: string, token: string) => string;
  onMessage?: (event: WebSocketMessageEvent) => void;
};

/**
 * Pure transport layer: manages the WebSocket lifecycle (connect, reconnect, auth,
 * circuit-breaker) without any buffering or business logic.
 *
 * Exposes stable connect/disconnect/send callbacks plus the current status.
 * onMessage fires for every inbound frame — callers own buffering/parsing.
 */
export function useWebSocketConnection(
  baseUrl: string,
  {
    autoReconnect = true,
    maxReconnectAttempts = DEFAULT_MAX_RECONNECT,
    extraHeaders,
    buildUrl,
    onMessage,
  }: WsConnectionOptions = {},
) {
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<WsStatus>('idle');
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTokenRef = useRef<string | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connectRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const scheduleReconnectRef = useRef<((immediate: boolean) => void) | undefined>(undefined);

  const clearTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  scheduleReconnectRef.current = (immediate: boolean) => {
    if (!autoReconnect) return;
    const attempt = ++reconnectAttemptRef.current;
    if (attempt > maxReconnectAttempts) {
      Telemetry.event('net.websocket.circuit_breaker.open', { domain: 'net', attempts: attempt }, 'error');
      return;
    }
    const delay = immediate ? 0 : Math.min(15_000, 1_000 * Math.pow(2, attempt));
    Telemetry.event('net.websocket.reconnect.scheduled', { domain: 'net', attempt, delay_ms: delay }, 'warning');
    clearTimer();
    reconnectTimerRef.current = setTimeout(() => connectRef.current?.(), delay);
  };

  connectRef.current = async () => {
    if (!baseUrl) return;
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) return;
    if (reconnectAttemptRef.current >= maxReconnectAttempts) {
      Telemetry.event('net.websocket.circuit_breaker.open', { domain: 'net', attempts: reconnectAttemptRef.current }, 'error');
      return;
    }

    setStatus('connecting');
    const connectStart = Date.now();

    try {
      const token = await AuthService.getToken();
      lastTokenRef.current = token;
      const url = buildUrl ? buildUrl(baseUrl, token!) : baseUrl;
      const ws = new WebSocket(url, undefined as any, {
        headers: { Authorization: `Bearer ${token}`, ...(extraHeaders ?? {}) },
      } as any);

      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('open');
        reconnectAttemptRef.current = 0;
        Telemetry.event('net.websocket.open', { domain: 'net', latency_ms: Date.now() - connectStart }, 'info');
        try {
          ws.send(JSON.stringify({ type: 'ping', at: Date.now() }));
        } catch (err) {
          Telemetry.error(err as Error, { event: 'net.websocket.ping.fail', domain: 'net' });
        }
      };

      ws.onmessage = (event) => {
        onMessageRef.current?.(event);
      };

      ws.onerror = () => {
        setStatus('error');
      };

      ws.onclose = async (e) => {
        setStatus('closed');
        Telemetry.event('net.websocket.close', { domain: 'net', code: e.code, reason: e.reason?.slice(0, 100) }, 'info');

        if (!autoReconnect) return;

        if (e.code != null && AUTH_CLOSE_CODES.includes(e.code)) {
          try {
            const fresh = await AuthService.getToken();
            if (fresh && fresh !== lastTokenRef.current) lastTokenRef.current = fresh;
            scheduleReconnectRef.current?.(true);
            return;
          } catch {
            Telemetry.event('net.websocket.auth.fail', { domain: 'net' }, 'error');
          }
        }
        scheduleReconnectRef.current?.(false);
      };
    } catch (err) {
      setStatus('error');
      scheduleReconnectRef.current?.(false);
    }
  };

  const connect = useCallback(() => { connectRef.current?.(); }, []);

  const disconnect = useCallback(() => {
    clearTimer();
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      setStatus('closing');
      wsRef.current.close();
    } else {
      wsRef.current?.close();
    }
    wsRef.current = null;
  }, []);

  const send = useCallback((data: string) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      Telemetry.event('net.websocket.send.not_open', { domain: 'net' }, 'warning');
      return;
    }
    try {
      wsRef.current.send(data);
    } catch (err) {
      Telemetry.error(err as Error, { event: 'net.websocket.send.fail', domain: 'net' });
    }
  }, []);

  useEffect(() => {
    connectRef.current?.();
    return () => {
      clearTimer();
      wsRef.current?.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  return { status, connect, disconnect, send };
}
